// src/Admin.js
// ─────────────────────────────────────────────────────────────────────────────
// Admin dashboard — reads walk data via Vercel serverless function (/api/getWalks)
// instead of directly from Firestore, so Firebase security rules can be locked.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';

function Admin() {
  const [walks, setWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const password = prompt('Enter admin password:');
    if (!password) {
      setError('Access cancelled.');
      setLoading(false);
      return;
    }

    fetch(`/api/getWalks?password=${encodeURIComponent(password)}`)
      .then(r => {
        if (r.status === 401) throw new Error('Wrong password.');
        if (!r.ok) throw new Error(`Server error: ${r.status}`);
        return r.json();
      })
      .then(data => setWalks(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const downloadCSV = () => {
    const headers = [
      'Date', 'Duration (seconds)', 'Start Emotion',
      'Final Emotion', 'Mid Check-ins', 'Meditation Used', 'Feedback'
    ];

    const rows = walks.map(walk => [
      walk.timestamp ? new Date(walk.timestamp._seconds * 1000).toLocaleString() : 'N/A',
      walk.duration || 0,
      walk.startEmotion || 'N/A',
      walk.finalEmotion || 'N/A',
      walk.emotionLogs ? walk.emotionLogs.join(' → ') : 'None',
      walk.meditationUsed ? 'Yes' : 'No',
      walk.feedback ? `"${walk.feedback.replace(/"/g, '""')}"` : 'None',
    ]);

    const csvContent = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `forestmood_data_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
      Loading data...
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: 'sans-serif', color: '#D0021B' }}>
      {error}
    </div>
  );

  return (
    <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ textAlign: 'center' }}>🌿 Mindful Steps — Admin</h2>
      <p style={{ textAlign: 'center', color: '#888', marginBottom: 24 }}>
        Total walks recorded: <strong>{walks.length}</strong>
      </p>

      <button onClick={downloadCSV} style={{
        display: 'block', margin: '0 auto 32px',
        padding: '12px 32px', borderRadius: 10, border: 'none',
        background: '#4A90D9', color: 'white', fontSize: 16,
        fontWeight: 'bold', cursor: 'pointer',
      }}>
        Download CSV
      </button>

      {walks.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999' }}>No walks recorded yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Date', 'Duration', 'Start', 'End', 'Check-ins', 'Meditation', 'Feedback'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {walks.map((walk, i) => (
                <tr key={walk.id} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={td}>
                    {walk.timestamp
                      ? new Date(walk.timestamp._seconds * 1000).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td style={td}>{Math.floor(walk.duration / 60)}m {walk.duration % 60}s</td>
                  <td style={td}>{walk.startEmotion || 'N/A'}</td>
                  <td style={td}>{walk.finalEmotion || 'N/A'}</td>
                  <td style={td}>{walk.emotionLogs ? walk.emotionLogs.join(' → ') : 'None'}</td>
                  <td style={td}>{walk.meditationUsed ? '🧘 Yes' : 'No'}</td>
                  <td style={td}>{walk.feedback || 'None'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #eee', fontWeight: 'bold' };
const td = { padding: '10px 12px', borderBottom: '1px solid #eee', verticalAlign: 'top' };

export default Admin;